/**
 * saas-auth product-admin identity client (WSRPDM-0018).
 *
 * Two surfaces (WSRPDM-0083):
 * - Tenant list / detail / create go through wsr-api's identity routes,
 *   `/admin/platform/identity/tenants[/{tenantId}]`, which `src/api/client.ts`
 *   routes to `apiBase()` — bearer-only, no CSRF. wsr-api returns saas-auth's
 *   `TenantDto` / page shapes verbatim with NO `{success,message,data}`
 *   envelope, so those three calls never `unwrap()`. Their errors arrive in
 *   wsr-api's flat `{code,message,status,details?}` body — read them with
 *   `wsrApiError()` (`../wsr/errors.ts`), not `ApiError.code`.
 * - Everything else (users, staff, role, status, remove, invitations) stays on
 *   the AUTH origin `/api/product-admin/**`, bearer+CSRF-routed by `client.ts`
 *   (WSRPDM-0010) — every write there already gets `X-CSRF-Token` from
 *   `request()`. Every response there is the ApiResponse envelope, so those
 *   calls `unwrap()` it.
 */
import { request, unwrap } from '../client';
import type { ApiResponse, PagedResponse } from '../types';
import type {
  AddMemberRequest,
  AddMemberResult,
  AssignableRole,
  CreateTenantRequest,
  InvitationStatus,
  TenantDto,
  TenantInvitationDto,
  TenantMemberDto,
  TenantMemberStatus,
} from './types';

const BASE = '/api/product-admin/tenants';
/** WSRPDM-0083 — wsr-api identity routes for tenant list / detail / create. */
const IDENTITY_BASE = '/admin/platform/identity/tenants';

export interface ListTenantsQuery {
  page?: number;
  size?: number;
  q?: string;
  /** SASATH-0253 — ACTIVE | INACTIVE | SUSPENDED. Omit for all statuses. */
  status?: string;
}

/** `GET /admin/platform/identity/tenants` (wsr-api, WSRPDM-0083) — paged, un-enveloped. */
export async function listTenants(query?: ListTenantsQuery): Promise<PagedResponse<TenantDto>> {
  return request<PagedResponse<TenantDto>>('GET', IDENTITY_BASE, {
    query: query as Record<string, string | number | undefined>,
  });
}

/** `GET /admin/platform/identity/tenants/{tenantId}` (wsr-api) — 404 if not in this product. */
export async function getTenant(tenantId: number): Promise<TenantDto> {
  return request<TenantDto>('GET', `${IDENTITY_BASE}/${tenantId}`);
}

/**
 * `POST /admin/platform/identity/tenants` (wsr-api) — 201 `TenantDto`; product
 * forced server-side from the token. Can take up to ~30 s; after a 201 the
 * tenant is already in the ops roster.
 */
export async function createTenant(body: CreateTenantRequest): Promise<TenantDto> {
  return request<TenantDto>('POST', IDENTITY_BASE, { json: body });
}

export interface ListMembersQuery {
  page?: number;
  size?: number;
  q?: string;
}

/**
 * `GET /api/product-admin/tenants/{tenantId}/users` — customer list after
 * SASATH-0319. `GUEST` members are filtered out of `content`;
 * `totalElements`/`totalPages` pass through unfiltered. Unused by this
 * console’s Staff tab (`listStaff` → `GET …/staff`). Do not point this at
 * `/staff`.
 */
export async function listMembers(
  tenantId: number,
  query?: ListMembersQuery,
): Promise<PagedResponse<TenantMemberDto>> {
  const res = await request<ApiResponse<PagedResponse<TenantMemberDto>>>(
    'GET',
    `${BASE}/${tenantId}/users`,
    { query: query as Record<string, string | number | undefined> },
  );
  const page = unwrap(res);
  return { ...page, content: page.content.filter((m) => m.role !== 'GUEST') };
}

/**
 * `GET /api/product-admin/tenants/{tenantId}/staff` — tenant wsr-admin
 * staff (SASATH-0326). Same paging query as `listMembers`. No GUEST
 * filter — staff rows are never GUEST. Fail closed on 404; never fall
 * back to `GET …/users`.
 */
export async function listStaff(
  tenantId: number,
  query?: ListMembersQuery,
): Promise<PagedResponse<TenantMemberDto>> {
  const res = await request<ApiResponse<PagedResponse<TenantMemberDto>>>(
    'GET',
    `${BASE}/${tenantId}/staff`,
    { query: query as Record<string, string | number | undefined> },
  );
  return unwrap(res);
}

/**
 * `POST /api/product-admin/tenants/{tenantId}/users` — §6. Discriminated by
 * response SHAPE, not the HTTP status alone: a `membershipId` field means an
 * existing account was added directly as a member (201); an `expiresAt`
 * field means an unknown email was invited instead (202, and the invitation
 * appears in the invitations list). `membershipId`/`expiresAt` are mutually
 * exclusive and always present on their respective DTOs, so this is
 * sufficient without extending `request()` to surface the raw status code.
 */
export async function addMember(
  tenantId: number,
  body: AddMemberRequest,
): Promise<AddMemberResult> {
  const res = await request<ApiResponse<TenantMemberDto | TenantInvitationDto>>(
    'POST',
    `${BASE}/${tenantId}/users`,
    { json: body },
  );
  const data = unwrap(res);
  if ('membershipId' in data) return { kind: 'member', member: data };
  return { kind: 'invitation', invitation: data as TenantInvitationDto };
}

/** `PUT /api/product-admin/tenants/{tenantId}/users/{membershipId}/role` — §6. */
export async function changeRole(
  tenantId: number,
  membershipId: number,
  role: AssignableRole,
): Promise<TenantMemberDto> {
  const res = await request<ApiResponse<TenantMemberDto>>(
    'PUT',
    `${BASE}/${tenantId}/users/${membershipId}/role`,
    { json: { role } },
  );
  return unwrap(res);
}

/** `PUT /api/product-admin/tenants/{tenantId}/users/{membershipId}/status` — §6. `DISABLED` blocks login. */
export async function setMemberStatus(
  tenantId: number,
  membershipId: number,
  status: TenantMemberStatus,
): Promise<TenantMemberDto> {
  const res = await request<ApiResponse<TenantMemberDto>>(
    'PUT',
    `${BASE}/${tenantId}/users/${membershipId}/status`,
    { json: { status } },
  );
  return unwrap(res);
}

/** `DELETE /api/product-admin/tenants/{tenantId}/users/{membershipId}` — §6. Deletes the membership, not the account. */
export async function removeMember(tenantId: number, membershipId: number): Promise<void> {
  await request<ApiResponse<unknown>>('DELETE', `${BASE}/${tenantId}/users/${membershipId}`);
}

/** `GET /api/product-admin/tenants/{tenantId}/invitations?status=` — §2. A JSON array, not paged; newest first. */
export async function listInvitations(
  tenantId: number,
  status?: InvitationStatus,
): Promise<TenantInvitationDto[]> {
  const res = await request<ApiResponse<TenantInvitationDto[]>>(
    'GET',
    `${BASE}/${tenantId}/invitations`,
    status ? { query: { status } } : {},
  );
  return unwrap(res);
}

/** `POST /api/product-admin/tenants/{tenantId}/invitations/{invitationId}/resend` — §3. Rotates + re-sends; no token ever returned. */
export async function resendInvitation(
  tenantId: number,
  invitationId: number,
): Promise<TenantInvitationDto> {
  const res = await request<ApiResponse<TenantInvitationDto>>(
    'POST',
    `${BASE}/${tenantId}/invitations/${invitationId}/resend`,
  );
  return unwrap(res);
}

/** `DELETE /api/product-admin/tenants/{tenantId}/invitations/{invitationId}` — §4. Idempotent on an already-revoked invite. */
export async function revokeInvitation(
  tenantId: number,
  invitationId: number,
): Promise<TenantInvitationDto> {
  const res = await request<ApiResponse<TenantInvitationDto>>(
    'DELETE',
    `${BASE}/${tenantId}/invitations/${invitationId}`,
  );
  return unwrap(res);
}
